import React, { useEffect, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useDocument, useCreateDocument, useUpdateDocument, useDocumentCategories } from '@/lib/api/hooks/document';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import { showToast } from '@/lib/toast';
import { ChevronLeftIcon } from '@/components/icons';
import { Button, CustomSelect, RichTextEditor } from '@/components/ui';
import FileUpload from '@/components/ui/FileUpload';
import Input from '@/components/ui/Input/Input';
import { Heading, Label, Paragraph } from '@/components/ui/Typography';
import TrashIcon from '@/assets/icons/TrashIcon';
import { LucideFiles } from 'lucide-react';
import { ReloadIcon } from '@/assets/icons';

interface UploadedFile {
  name: string;
  size: number;
  url: string;
}

const DocumentFormSchema = z.object({
  title: z.string().trim().min(1, 'Title is required'),
  documentCategory: z.string().trim().min(1, 'Category is required'),
  description: z.string().optional(),
  fileUrl: z.string().trim().min(1, 'Document file is required')
});

type DocumentFormData = z.infer<typeof DocumentFormSchema>;

const DEFAULT_VALUES: DocumentFormData = {
  title: '',
  documentCategory: '',
  description: '',
  fileUrl: ''
};

const DocumentEditor: React.FC = () => {
  const { documentId } = useParams<{ documentId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const isEditMode = !!documentId;
  const existingDocument = location.state?.document;
  const fileUploadRef = useRef<HTMLDivElement>(null);

  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [uploadKey, setUploadKey] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const { data: documentData, isLoading } = useDocument(documentId);
  const { data: categoriesData, isLoading: isLoadingCategories } = useDocumentCategories();
  const createMutation = useCreateDocument();
  const updateMutation = useUpdateDocument();
  const uploadMutation = useFileUpload();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<DocumentFormData>({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(DocumentFormSchema),
    mode: 'onChange'
  });

  const watchTitle = useWatch({ control, name: 'title' });

  // Document categories from API
  const categoryOptions = useMemo(
    () =>
      (categoriesData?.data ?? []).map((cat: { value: string; displayName: string }) => ({
        value: cat.value,
        label: cat.displayName
      })),
    [categoriesData?.data]
  );

  const isBusy = isSubmitting || isUploading || isDraftSaving || isPublishing;

  useEffect(() => {
    if (isEditMode && (documentData?.data || existingDocument)) {
      const doc = documentData?.data || existingDocument;
      const description =
        typeof (doc as { description?: unknown }).description === 'string'
          ? ((doc as { description?: string }).description ?? '')
          : '';

      reset({
        title: doc.title || '',
        documentCategory: doc.documentCategory || '',
        description,
        fileUrl: doc.documentUrl || ''
      });

      if (doc.documentUrl) {
        const urlParts = doc.documentUrl.split('/');
        const filename = urlParts[urlParts.length - 1] || 'document.pdf';
        setUploadedFile({
          name: filename,
          size: 0,
          url: doc.documentUrl
        });
      } else {
        setUploadedFile(null);
      }

      setUploadKey((prev) => prev + 1);
    }
  }, [isEditMode, documentData, existingDocument, reset]);

  const uploadFileToServer = async (file: File) => {
    setIsUploading(true);
    try {
      const uploadResult = await uploadMutation.mutateAsync({
        file,
        category: 'DOCUMENT'
      });

      if (!uploadResult.success || !uploadResult.data) {
        throw new Error('File upload failed');
      }

      const fileUrl = uploadResult.data.fileUrl;

      if (!fileUrl) {
        throw new Error('File URL not found in response');
      }

      setUploadedFile({
        name: file.name,
        size: file.size,
        url: fileUrl
      });
      setValue('fileUrl', fileUrl, { shouldValidate: true, shouldDirty: true });
      setUploadKey((prev) => prev + 1);

      showToast.success('File uploaded successfully');
    } catch (error) {
      console.error('File upload failed:', error);
      showToast.error('Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelection = async (files: File[] | null) => {
    if (!files || files.length === 0) {
      return;
    }

    await uploadFileToServer(files[0]);
  };

  const handleDeleteFile = () => {
    setUploadedFile(null);
    setValue('fileUrl', '', { shouldValidate: true, shouldDirty: true });
    setUploadKey((prev) => prev + 1);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return 'Unknown size';
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(0)}kb`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(0)}mb`;
  };

  const submitWithStatus = async (formData: DocumentFormData, status: 'DRAFT' | 'PUBLISH') => {
    const setLoading = status === 'DRAFT' ? setIsDraftSaving : setIsPublishing;
    setLoading(true);

    try {
      const payload = {
        title: formData.title.trim(),
        documentCategory: formData.documentCategory,
        fileUrl: formData.fileUrl,
        description: formData.description || "",
        status
      };

      if (documentId) {
        await updateMutation.mutateAsync({
          documentId,
          data: payload
        });
        showToast.success(`Document ${status === 'PUBLISH' ? 'published' : 'saved as draft'} successfully`);
      } else {
        await createMutation.mutateAsync(payload);
        showToast.success(`Document ${status === 'PUBLISH' ? 'published' : 'created as draft'} successfully`);
      }

      navigate('/admin/documents');
    } catch (error) {
      console.error('Failed to save document:', error);
      showToast.error(error instanceof Error ? error.message : 'Failed to save document');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading && documentId) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-wine-100"></div>
          <p className="mt-4 text-sm text-gray-500">Loading document...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl relative">
      <header className="p-4 flex sticky top-0 z-10 bg-white justify-between items-center border-b border-gray-200">
        <button
          onClick={() => navigate('/admin/documents')}
          className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
          disabled={isBusy}
        >
          <ChevronLeftIcon className="w-5 h-5" />
          Go back
        </button>
        <div className="flex items-center gap-4">
          <Button
            type='button'
            variant='secondary'
            onClick={() => {
              void handleSubmit((data) => submitWithStatus(data, 'DRAFT'))();
            }}
            disabled={isBusy}
            loading={isDraftSaving}
          >
            Save as Draft
          </Button>
          <Button
            type='button'
            onClick={() => {
              void handleSubmit((data) => submitWithStatus(data, 'PUBLISH'))();
            }}
            disabled={isBusy}
            loading={isPublishing || isUploading}
          >
            Publish
          </Button>
        </div>
      </header>
          <div className="max-w-5xl mx-auto">

          
      <div className='border-b border-gray-200 px-4 py-4 md:px-7'>
        <h1 className='mb-3 text-2xl font-bold text-gray-900'>{isEditMode ? 'Edit Document' : 'New Document'}</h1>
        <nav className='flex items-center gap-2 text-sm'>
          <button
            onClick={() => navigate('/admin/documents')}
            className='text-wine-100 hover:text-wine-200 hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
            disabled={isBusy}
          >
            All Documents
          </button>
          {isEditMode ? (
            <>
              <span className='text-gray-400'>/</span>
              <button
                onClick={() => navigate(`/admin/documents/${documentId}`)}
                className='text-wine-100 hover:text-wine-200 hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
                disabled={isBusy}
              >
                {(watchTitle || '').trim() || 'Document'}
              </button>
              <span className='text-gray-400'>/</span>
              <span className='text-gray-600'>Edit</span>
            </>
          ) : (
            <>
              <span className='text-gray-400'>/</span>
              <span className='text-gray-600'>New Document</span>
            </>
          )}
        </nav>
      </div>

      <div className="p-6 md:p-10 max-w-3xl mx-auto space-y-6">
        <form onSubmit={(event) => event.preventDefault()} className='space-y-6'>
          <div className='rounded-xl border border-gray-200 bg-white p-4 md:p-5'>
            <Heading level={4} className='text-gray-900'>
              Document File
            </Heading>
            <Paragraph className='mt-1 text-sm text-gray-500'>
              Upload the procurement document (PDF preferred)
            </Paragraph>

            <Controller
              name='fileUrl'
              control={control}
              render={() => (
                <FileUpload
                  key={uploadKey}
                  ref={fileUploadRef}
                  onChange={handleFileSelection}
                  disabled={isBusy}
                  allowedFileTypes={['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
                  $maxSizeMB={50}
                  placeholder=''
                  containerClassName='mt-4 min-h-56 flex flex-col items-center justify-center gap-3 text-center bg-[#ebe9e938] border-gray-200'
                >
                  <LucideFiles className='h-16 w-16 ' strokeWidth={1} />
                  <Paragraph className='font-semibold text-gray-700'>Drag and drop your file here</Paragraph>
                  <Paragraph className='text-xs text-gray-500'>
                    Supported formats: PDF, DOC, DOCX. Max size: 50MB
                  </Paragraph>
                  <span className=' text-sm'>or</span>
                  <Button type='button' variant='secondary' className='text-green-700'>
                    Browse Files
                  </Button>
                </FileUpload>
              )}
            />

            {uploadedFile ? (
              <div className='mt-4 flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3'>
                <div className='flex items-center gap-3'>
                  <div className='flex h-10 w-10 items-center justify-center rounded bg-red-50 text-xs font-semibold text-red-600'>
                    PDF
                  </div>
                  <div>
                    <Paragraph className='font-medium text-gray-900'>{uploadedFile.name}</Paragraph>
                    <Paragraph className='text-xs text-gray-500'>{formatFileSize(uploadedFile.size)}</Paragraph>
                  </div>
                </div>

                <div className='flex items-center gap-2'>
                  <Button
                    type='button'
                    variant='secondary'
                    onClick={() => fileUploadRef.current?.click()}
                    disabled={isBusy}
                    className='text-green-700'
                  >
                    <ReloadIcon className='h-5 w-5' strokeWidth={2} />
                    Replace
                  </Button>
                  <button
                    type='button'
                    onClick={handleDeleteFile}
                    disabled={isBusy}
                    className='rounded-md border border-red-100 p-2 hover:bg-red-100 disabled:opacity-50'
                    aria-label='Remove uploaded file'
                  >
                    <TrashIcon fill='#DE534E' className='h-6 w-6' />
                  </button>
                </div>
              </div>
            ) : null}

            {errors.fileUrl ? <Paragraph className='mt-2 text-sm text-red-600'>{errors.fileUrl.message}</Paragraph> : null}
          </div>

          <Controller
            name='title'
            control={control}
            render={({ field }) => (
              <Input
                label='Title'
                required
                placeholder='Enter'
                value={field.value}
                onChange={field.onChange}
                error={errors.title?.message}
                disabled={isBusy}
                $marginbottom='0'
              />
            )}
          />

          <Controller
            name='documentCategory'
            control={control}
            render={({ field }) => (
              <div>
                <Label className='mb-2 block'>Category</Label>
                <CustomSelect
                  id='documentCategory'
                  name={field.name}
                  value={field.value}
                  onChange={(value) => field.onChange(Array.isArray(value) ? value[0] ?? '' : value)}
                  onBlur={() => field.onBlur()}
                  options={categoryOptions}
                  placeholder={isLoadingCategories ? 'Loading categories...' : 'Create or Select Category'}
                  disabled={isBusy || isLoadingCategories}
                />
                {errors.documentCategory ? (
                  <Paragraph className='mt-2 text-sm text-red-600'>{errors.documentCategory.message}</Paragraph>
                ) : null}
              </div>
            )}
          />

{/* This is hidden until the field is configured from backend */}
          <div className='rounded-xl border border-gray-200 bg-white p-4 md:p-5' style={{display: "none"}}>
            <Label className='mb-2 block'>Description / Notes</Label>
            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  value={field.value || ''}
                  onChange={field.onChange}
                  disabled={isBusy}
                  placeholder='Write description or notes (optional)'
                  className='border-gray-200'
                  toolbarContainerClassName='bg-gray-50 border-gray-200 px-4 py-2'
                  contentAreaClassName='min-h-[180px] max-h-[420px] p-3'
                />
              )}
            />
          </div>
        </form>
      </div>
      </div>
    </div>
  );
};

export default DocumentEditor;
